import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@tadagram/ui';
import { Dashboard, Conversations, Scenarios } from './pages';
import ChatbotOverviewPage from './pages/chatbot/ChatbotOverviewPage';
import ChatbotConfigPage from './pages/chatbot/ChatbotConfigPage';
import ChatbotMessagesPage from './pages/chatbot/ChatbotMessagesPage';
import ChatbotSettingsPage from './pages/chatbot/ChatbotSettingsPage';
import ChatbotTestPage from './pages/chatbot/ChatbotTestPage';
import ChatbotKnowledgeBasePage from './pages/chatbot/ChatbotKnowledgeBasePage';
import ChatbotProductsPage from './pages/chatbot/ChatbotProductsPage';
import ChatbotInterviewPage from './pages/chatbot/ChatbotInterviewPage';
import { authService } from './services/auth';
import { apiService } from './services/api';
import { workerApi, workerApiService } from './services/workerApi';
import { AppHeader } from './components/layout/AppHeader';
import { PlatformSidebar } from './components/layout/PlatformSidebar';
import { AccountSidebar } from './components/account/AccountSidebar';
import { ChatbotDetail } from './components/chatbot/ChatbotDetail';
import { useSSE } from './hooks/useSSE';
import { isTrustedShellOrigin } from './constants/origins';
import i18n from './i18n';
import { useIframeNavigationSync } from './hooks/useIframeNavigationSync';

/** Null-rendering component — must be inside <Router> to use useLocation. */
function IframeNavSync() {
  useIframeNavigationSync();
  return null;
}
import { mapApiPlatform } from './types/platform';
import type { Platform } from './types/platform';
import { WorkerConnectModal } from './components/worker/WorkerConnectModal';
import './App.css';

// ── WorkerGuard ──────────────────────────────────────────────────────────────
// After auth, the app checks whether the current user has an active chatbot
// worker assignment in svc-business-chatbot (MySQL chatbot_worker_assignments).
// If not, it auto-requests allocation via POST /api/v1/worker/allocate.
// Column 3 (main content) is LOCKED until the worker is ready.
//
// States:
//   idle        — auth not yet complete / check not started
//   checking    — GET /api/v1/my-chatbots in-flight
//   allocating  — no worker found, POST /api/v1/worker/allocate in-flight
//   ready       — worker is active and assigned, UI unlocked
//   unavailable — allocation failed (no free workers), UI locked with error
type WorkerStatus = 'idle' | 'checking' | 'allocating' | 'ready' | 'unavailable';

// Version: 1.1.0
// Auth model: PORTAL — token đến từ biztada-app qua postMessage hoặc ?code=
// App này KHÔNG có login riêng, chỉ hoạt động khi được embed từ biztada.com
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // 3-column layout state
  const [activePlatform, setActivePlatform] = useState<string>('facebook');
  
  // Real data from API
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // True after fetchInitialData completes (setDirectMode done + platforms loaded).
  // Child components (Dashboard, PageDashboard) gate worker API calls on this flag
  // to avoid calling worker endpoints before the tunnel URL is set.
  const [workerApiReady, setWorkerApiReady] = useState(false);

  // Worker readiness: null = not checked yet, true = has worker, false = need to allocate
  const [workerReady, setWorkerReady] = useState<boolean | null>(null);

  // Guard: fetchInitialData runs exactly once after the first successful auth.
  // Prevents duplicates from: IFRAME_READY retry loop, multiple postMessage events,
  // or re-renders calling fetchInitialData more than once.
  const dataFetchedRef = useRef(false);

  // Worker guard state — checked once after first successful auth.
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus>('idle');
  const [workerError, setWorkerError] = useState<string>('');
  const workerCheckDoneRef = useRef(false);
  
  // SSE: Real-time updates from backend
  useSSE({
    enabled: isAuthenticated,
    onConversationNew: (_data) => {
      fetchConversations();
    },
    onMessageReceived: (data) => {
      setConversations(prev => prev.map(conv => 
        conv.id === data.conversation_id
          ? { ...conv, last_message: data.content, last_message_at: data.created_at, unread_count: (conv.unread_count || 0) + 1 }
          : conv
      ));
    },
    onMessageSent: (data) => {
      setConversations(prev => prev.map(conv => 
        conv.id === data.conversation_id
          ? { ...conv, last_message: data.content, last_message_at: data.created_at }
          : conv
      ));
    },
    onPlatformStatus: (data) => {
      setPlatforms(prev => prev.map(platform => 
        platform.id === data.platform_id
          ? { ...platform, status: data.status, updated_at: data.timestamp }
          : platform
      ));
    },
  });
  
  // ── Auth: nhận token từ biztada-app ────────────────────────────────────────
  // Ưu tiên 1: postMessage từ parent window (khi embed trong iframe) → BIZTADA_AUTH_TOKEN
  // Ưu tiên 2: ?code= trong URL (legacy, backwards compat)
  // Ưu tiên 3: session token còn lưu trong localStorage từ lần trước

  useEffect(() => {
    // ── DEV BYPASS ─────────────────────────────────────────────────────────────
    // Chỉ chạy trong `vite dev` (import.meta.env.DEV === true).
    // Vite/Rollup loại bỏ toàn bộ khối này khi build production — zero footprint.
    //
    // Cách dùng:
    //   1. cp .env.example .env.local
    //   2. Thêm: VITE_DEV_TOKEN=<JWT lấy từ biztada.com DevTools → Application → localStorage>
    //   3. npm run dev → vào thẳng giao diện, không cần login
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_TOKEN) {
      authService.setAuthToken(
        import.meta.env.VITE_DEV_TOKEN as string,
        import.meta.env.VITE_DEV_BUSINESS_ID as string | undefined,
      );
      apiService.setAuthToken(import.meta.env.VITE_DEV_TOKEN as string);
      setIsAuthenticated(true);
      setIsLoading(false);
      fetchInitialData().then((ok) => {
        if (ok) dataFetchedRef.current = true;
      });
      return;
    }
    // ───────────────────────────────────────────────────────────────────────────

    let iframeMode = false;

    // postMessage handler: biztada-app shell sends JWT via BIZTADA_AUTH_TOKEN
    // and language changes via BIZTADA_LANGUAGE_CHANGE
    const handleMessage = async (event: MessageEvent) => {
      // ── Language change ────────────────────────────────────────────────────
      if (event.data?.type === 'BIZTADA_LANGUAGE_CHANGE') {
        if (!isTrustedShellOrigin(event.origin)) return;
        const locale: string = event.data?.locale;
        if (locale && ['vi', 'en', 'ja', 'ko', 'zh'].includes(locale)) {
          try { localStorage.setItem('biztada-language', locale); } catch {}
          i18n.changeLanguage(locale);
        }
        return;
      }

      if (event.data?.type !== 'BIZTADA_AUTH_TOKEN') return;
      if (!event.data?.token) return;

      if (!isTrustedShellOrigin(event.origin)) {
        console.warn('[Auth] Rejected postMessage from unknown origin:', event.origin);
        return;
      }

      const token: string = event.data.token;
      let businessId: string | undefined = event.data.businessId;
      
      // Fallback: if businessId not provided, try to get from URL or localStorage
      if (!businessId) {
        const urlParams = new URLSearchParams(window.location.search);
        businessId = urlParams.get('business_id') || undefined;
        if (!businessId) {
          businessId = localStorage.getItem('oauth_business_id') || undefined;
        }
      }
      
      authService.setAuthToken(token, businessId);
      apiService.setAuthToken(token);
      console.log('[Auth] JWT set from shell postMessage, businessId:', businessId);
      setIsAuthenticated(true);
      setIsLoading(false);
      // Only fetch initial data once — subsequent token refreshes from the shell
      // (IFRAME_READY retry loop) must NOT trigger additional API calls.
      // latch only on success: if stale cached token caused 401, fresh token must retry.
      if (!dataFetchedRef.current) {
        const ok = await fetchInitialData();
        if (ok) dataFetchedRef.current = true;
      }
      // ACK lại cho parent
      (event.source as Window | null)?.postMessage(
        { type: 'APP_AUTH_READY', app: 'business-chatbot' },
        event.origin,
      );
    };

    // Register handler BEFORE initAuth so it's ready when IFRAME_READY fires
    window.addEventListener('message', handleMessage);

    const initAuth = async () => {
      try {
        // 1. OAuth2 code từ biztada redirect: ?code=...&client_id=... (legacy)
        const urlParams = new URLSearchParams(window.location.search);
        const oauthCode = urlParams.get('code');
        const clientId = urlParams.get('client_id');
        if (oauthCode && clientId) {
          const ok = await authService.exchangeOAuthCode(oauthCode, clientId);
          window.history.replaceState({}, '', window.location.pathname);
          if (ok) {
            const token = authService.getSessionToken();
            if (token) apiService.setAuthToken(token);
            setIsAuthenticated(true);
            await fetchInitialData();
          }
          return;
        }

        // 2. Token trực tiếp trong ?token= (legacy)
        const directToken = urlParams.get('token');
        if (directToken) {
          authService.setAuthToken(directToken);
          apiService.setAuthToken(directToken);
          window.history.replaceState({}, '', window.location.pathname);
          setIsAuthenticated(true);
          await fetchInitialData();
          return;
        }

        // 3. Session cũ còn hợp lệ
        const hasSession = await authService.checkWebSession();
        if (hasSession) {
          const token = authService.getSessionToken();
          if (token) apiService.setAuthToken(token);
          setIsAuthenticated(true);
          await fetchInitialData();
          return;
        }

        // 4. Đang chạy trong iframe → signal shell để nhận BIZTADA_AUTH_TOKEN
        if (window !== window.parent) {
          iframeMode = true;

          // Fast resume: dùng cached JWT ngay lập tức thay vì chờ shell gửi lại
          if (authService.loadCachedIframeToken()) {
            const cached = authService.getSessionToken();
            if (cached) apiService.setAuthToken(cached);
            setIsAuthenticated(true);
            setIsLoading(false);
            if (!dataFetchedRef.current) {
              const ok = await fetchInitialData();
              if (ok) dataFetchedRef.current = true;
              // If fetch failed (stale token → 401), ref stays false so the
              // fresh token from the shell will trigger a retry in handleMessage.
            }
            // Vẫn gửi IFRAME_READY để shell push fresh token
          }

          // IFRAME_READY retry mỗi 1s × 30 lần (shell cần 200-2000ms để fetch JWT)
          let shellTokenReceived = false;
          const origHandler = handleMessage;
          const retryHandler = (event: MessageEvent) => {
            origHandler(event);
            if (event.data?.type === 'BIZTADA_AUTH_TOKEN' && event.data?.token) {
              shellTokenReceived = true;
            }
          };
          window.removeEventListener('message', handleMessage);
          window.addEventListener('message', retryHandler);

          const sendReady = () =>
            window.parent.postMessage({ type: 'IFRAME_READY', app: 'chatbot' }, '*');
          sendReady();
          let retries = 0;
          const retryInterval = setInterval(() => {
            if (shellTokenReceived) { clearInterval(retryInterval); return; }
            retries++;
            if (retries <= 30) sendReady();
            else clearInterval(retryInterval);
          }, 1000);

          // Fallback: stop spinner sau 10s nếu không nhận được token
          setTimeout(() => {
            if (!authService.isAuthenticated()) setIsLoading(false);
          }, 10000);

          return;
        }

        // 5. Standalone, không auth — trong local dev vẫn fetch data
        // vì worker API không yêu cầu JWT.
        setIsAuthenticated(false);
        await fetchInitialData();
      } catch (error) {
        console.error('Auth init failed:', error);
        setIsAuthenticated(false);
      } finally {
        if (!iframeMode) setIsLoading(false);
      }
    };

    initAuth();
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  // Returns true on success (platforms API responded), false on auth failure (401/403).
  // Callers must NOT latch dataFetchedRef until this returns true — a 401 from a stale
  // cached token must leave the ref unlocked so the fresh shell token can retry.
  const fetchInitialData = async (): Promise<boolean> => {
    setIsDataLoading(true);

    // 1. Worker URL fetch removed — we now rely strictly on the backend proxy
    // to route requests to the correct worker, ensuring backend remains the 
    // single source of truth for all data (especially platforms).
    // 2. Load platforms (svc-business-chatbot DB via proxy — not affected by direct mode)
    let success = false;
    try {
      const platformsData = await workerApi.getPlatforms();
      const mapped = (platformsData.data || []).map(mapApiPlatform);
      console.log('[App] platforms loaded:', mapped.length, mapped);
      setPlatforms(mapped);
      success = true;
    } catch (error) {
      console.error('❌ Failed to fetch platforms:', error);
    } finally {
      setIsDataLoading(false);
    }
    if (!success) return false;

    // Direct mode is removed. Worker API calls are always safe to make via proxy.
    setWorkerApiReady(true);

    // Check worker readiness — show allocation modal if user has no active worker.
    // Best-effort: don't block if my-chatbots call fails.
    try {
      const { workers } = await apiService.getMyChatbots() as any;
      setWorkerReady(workers.some((w: any) => w.is_active));
    } catch {
      setWorkerReady(true); // assume ready on error to avoid false modal
    }
    // Conversations fetched separately so a failure doesn't block platforms
    try {
      const conversationsData = await workerApi.getConversations({ limit: 50, status: 'active' });
      setConversations(conversationsData.conversations || []);
    } catch {
      // non-critical — sidebar still works without conversations
    }

    return true;
  };
  
  // ── Worker guard ──────────────────────────────────────────────────────────
  // Checks MySQL assignment then auto-allocates if none found.
  // Called once automatically when isAuthenticated becomes true.
  const checkAndAllocateWorker = async () => {
    setWorkerStatus('checking');
    try {
      const data: any = await apiService.getMyChatbots();
      const workers: any[] = data?.workers ?? [];
      if (workers.length > 0 && workers.some((w: any) => w.is_active)) {
        setWorkerStatus('ready');
        return;
      }
    } catch {
      // Check failed (network / 401 already handled by interceptor) — fall through to allocate
    }

    // No active assignment found → auto-request allocation (synchronous, 30 s timeout on backend)
    setWorkerStatus('allocating');
    try {
      await apiService.requestWorkerAllocation();
      setWorkerStatus('ready');
    } catch (e: any) {
      const errorCode: string = e?.response?.data?.error ?? '';
      const msg: string =
        errorCode === 'PORTAL_WORKER_OFFLINE'
          ? 'Portal riêng tư của bạn chưa khởi động. Vui lòng bật Worker Portal trước rồi thử lại.'
          : e?.response?.data?.message ||
            'Không thể kết nối chatbot worker. Vui lòng thử lại hoặc liên hệ quản trị viên.';
      setWorkerError(msg);
      setWorkerStatus('unavailable');
    }
  };

  // Trigger worker check once after auth succeeds (not per-render).
  useEffect(() => {
    if (!isAuthenticated || workerCheckDoneRef.current) return;
    workerCheckDoneRef.current = true;
    checkAndAllocateWorker();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchConversations = async () => {
    try {
      const response = await workerApi.getConversations({ limit: 50, status: 'active' });
      setConversations(response.conversations || []);
    } catch (error) {
      console.error('❌ Failed to fetch conversations:', error);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Chưa xác thực — không có login riêng; chỉ hoạt động khi mở từ biztada.com
  if (false && !isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3 text-gray-500">
        <div className="text-[32px]">🤖</div>
        <p className="font-semibold text-base text-gray-800">Business Chatbot</p>
        <p className="text-sm text-center">
          Vui lòng truy cập qua{' '}
          <a href="https://biztada.com" className="text-primary">biztada.com</a>
        </p>
      </div>
    );
  }
  
  // Main app (authenticated)
  return (
    <>
    {/* Worker allocation gate: shown over the entire app until user has an active worker */}
    {isAuthenticated && workerReady === false && (
      <WorkerConnectModal
        onConnected={async () => {
          setWorkerReady(null);
          await fetchInitialData();
        }}
      />
    )}
    <Router>
      <IframeNavSync />
      <Routes>
        {/* Everything else: 3-column app layout */}
        <Route
          path="*"
          element={
            <AppLayout
              platforms={platforms}
              isDataLoading={isDataLoading}
              activePlatform={activePlatform}
              onPlatformChange={(id) => { setActivePlatform(id); }}
              fetchConversations={fetchConversations}
              fetchPlatforms={fetchInitialData}
              workerApiReady={workerApiReady}
              workerStatus={workerStatus}
              workerError={workerError}
              onRetryWorker={() => {
                workerCheckDoneRef.current = false;
                setWorkerError('');
                checkAndAllocateWorker();
              }}
            />
          }
        />
      </Routes>
    </Router>
    </>
  );
}

// ── AppLayout ─────────────────────────────────────────────────────────────────
// Rendered inside <Router> so it can use useNavigate / useMatch etc.
interface AppLayoutProps {
  platforms: any[];
  isDataLoading: boolean;
  activePlatform: string;
  onPlatformChange: (id: string) => void;
  fetchConversations: () => void;
  fetchPlatforms: () => void;
  workerApiReady: boolean;
  workerStatus: WorkerStatus;
  workerError: string;
  onRetryWorker: () => void;
}

function AppLayout({ platforms, isDataLoading, activePlatform, onPlatformChange, fetchConversations, fetchPlatforms, workerApiReady, workerStatus, workerError, onRetryWorker }: AppLayoutProps) {
  const navigate = useNavigate();

  // ── Listen for BIZTADA_CHATBOT_CONNECTED from parent (post-FB OAuth) ──────
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'BIZTADA_CHATBOT_CONNECTED') return;
      if (!isTrustedShellOrigin(event.origin)) return;
      const platformId = event.data?.platformId;
      if (platformId) {
        navigate(`/chatbot/${platformId}/interview`, { replace: true });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate]);

  return (
    <div className="app-container">
      {/* Column 1: Platform Sidebar */}
      <PlatformSidebar
        platforms={platforms}
        activePlatform={activePlatform}
        onPlatformChange={(platformId) => {
          onPlatformChange(platformId);
          navigate('/');          // leave any /chatbot/... page
          fetchConversations();
        }}
      />

      {/* Column 2: Account Sidebar */}
      <AccountSidebar
        activePlatform={activePlatform}
        platforms={platforms}
        isLoading={isDataLoading}
        onRefresh={fetchPlatforms}
      />

      {/* Column 3: Main Content */}
      <div className="main-content w-full" style={{ position: 'relative' }}>
        {/* ── Worker Guard overlay ──────────────────────────────────────────────
            Locks column 3 while the chatbot worker is being checked/allocated.
            Sidebars (col 1 + 2) remain interactive so the user can manage
            platforms even while the worker is starting up. */}
        {(workerStatus === 'checking' || workerStatus === 'allocating') && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(2px)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12,
          }}>
            <LoadingSpinner />
            <p style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
              {workerStatus === 'checking' ? 'Đang kiểm tra chatbot worker...' : 'Đang kết nối chatbot worker...'}
            </p>
            <p style={{ fontSize: 12, color: '#6B7280' }}>
              {workerStatus === 'allocating' ? 'Lần đầu sử dụng — quá trình này có thể mất 10–30 giây.' : ''}
            </p>
          </div>
        )}
        {workerStatus === 'unavailable' && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(2px)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 16,
          }}>
            <div style={{ fontSize: 36 }}>🤖</div>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Không có chatbot worker sẵn sàng</p>
            <p style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', maxWidth: 360 }}>
              {workerError || 'Không có chatbot worker nào sẵn sàng. Vui lòng thử lại sau hoặc liên hệ quản trị viên.'}
            </p>
            <button
              onClick={onRetryWorker}
              style={{
                padding: '8px 20px', borderRadius: 8,
                background: '#2563EB', color: '#fff',
                border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              }}
            >
              Thử lại
            </button>
          </div>
        )}
        <AppHeader />
        <div className="main-content-routes">
          <Routes>
            {/* ── Chatbot layout route — ChatbotDetail = shell with <Outlet /> ── */}
            <Route path="/chatbot/:id" element={<ChatbotDetail platforms={platforms} />}>
            <Route index element={<ChatbotOverviewPage />} />
            <Route path="config"         element={<ChatbotConfigPage />} />
            <Route path="products"       element={<ChatbotProductsPage />} />
            <Route path="messages"       element={<ChatbotMessagesPage />} />
            <Route path="knowledge-base" element={<ChatbotKnowledgeBasePage />} />
            <Route path="interview"      element={<ChatbotInterviewPage />} />
            <Route path="test"           element={<ChatbotTestPage />} />
            <Route path="settings"       element={<ChatbotSettingsPage />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>

          {/* ── App pages — share AppHeader via layout route ── */}
          <Route element={<AppPagesLayout />}>
            <Route index element={<Dashboard platforms={platforms} onRefresh={fetchPlatforms} dataReady={workerApiReady} />} />
            <Route
              path="/conversations"
              element={<Conversations conversationId={null} onRefresh={fetchConversations} />}
            />
            <Route path="/scenarios"      element={<Scenarios />} />
          </Route>
        </Routes>
        </div>
      </div>
    </div>
  );
}

// Shared layout wrapper for app-level pages (Dashboard, Settings, etc.)
function AppPagesLayout() {
  return (
    <div className="main-content-body">
      <Outlet />
    </div>
  );
}

export default App;
